import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Badge,
  Table,
  IconButton,
  Tooltip,
  DataList,
  Spinner,
  TextField,
  Theme,
} from '@radix-ui/themes';
import {
  ListBulletIcon,
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
  LockClosedIcon,
  CalendarIcon,
  PersonIcon,
  Cross2Icon,
  CaretUpIcon,
  CaretDownIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';
import { AddressDisplay } from '../../components/AddressDisplay';
import { EntityDisplay } from '../../components/EntityDisplay';
import { useTheme } from '../../contexts/ThemeContext';
import DetailPageLayout from '../../components/DetailPageLayout';
import {
  useGetListQuery,
  GetListQuery,
} from '../../graphql/queries/GetList.generated';
import { useRemoveItemFromListMutation } from '../../graphql/mutations/RemoveItemFromList.generated';
import { useAddItemToListMutation } from '../../graphql/mutations/AddItemToList.generated';
import { SortDirection } from '../../types';

type ListItem = NonNullable<NonNullable<GetListQuery['list']>['items']['edges'][number]['node']>;

const ListDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { effectiveTheme } = useTheme();
  const [sortColumn, setSortColumn] = useState<'entity' | 'address' | 'added' | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Asc);

  // Build sort parameters for GraphQL query
  const sortParams = React.useMemo(() => {
    if (!sortColumn) return undefined;

    const fieldMap = {
      entity: 'name',
      address: 'value',
      added: 'createdAt',
    };

    return {
      field: fieldMap[sortColumn],
      direction: sortDirection,
      rel: sortColumn === 'entity' ? 'entity' : undefined,
    };
  }, [sortColumn, sortDirection]);

  const { data, loading, error, refetch } = useGetListQuery({
    variables: {
      id: id || '',
      itemSort: sortParams,
    },
    skip: !id,
  });
  const [removeItem] = useRemoveItemFromListMutation();
  const [addItem, { loading: addingItem }] =
    useAddItemToListMutation();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newItemValue, setNewItemValue] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const list = data?.list;
  const isAddressList = list?.itemType === 'ADDRESS';

  // Debug: Check for duplicate IDs
  React.useEffect(() => {
    if (list?.items.edges) {
      const ids = list.items.edges.map(({ node }) => node.id);
      const uniqueIds = new Set(ids);
      if (ids.length !== uniqueIds.size) {
        console.error('Duplicate IDs detected in list items:', {
          totalItems: ids.length,
          uniqueItems: uniqueIds.size,
          duplicates: ids.filter((id, index) => ids.indexOf(id) !== index),
          allIds: ids,
        });
      }
    }
  }, [list?.items.edges]);

  const handleSort = (column: 'entity' | 'address' | 'added') => {
    if (sortColumn === column) {
      // Cycle through: Asc -> Desc -> No Sort
      if (sortDirection === SortDirection.Asc) {
        setSortDirection(SortDirection.Desc);
      } else {
        // Clear sorting and reset direction
        setSortColumn(null);
        setSortDirection(SortDirection.Asc);
      }
    } else {
      // Set new field with ascending as default
      setSortColumn(column);
      setSortDirection(SortDirection.Asc);
    }
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);

    if (!list || !newItemValue.trim()) {
      setAddError('Value is required');
      return;
    }

    // Validate based on list type
    if (isAddressList) {
      const address = newItemValue.trim().toLowerCase();
      if (!/^0x[a-f0-9]{40}$/i.test(address)) {
        setAddError('Invalid Ethereum address format');
        return;
      }
    }

    try {
      await addItem({
        variables: {
          input: {
            listId: list.id,
            value: isAddressList ? newItemValue.trim().toLowerCase() : newItemValue.trim()
          }
        },
      });
      setNewItemValue('');
      setIsAddDialogOpen(false);
      refetch();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add item');
    }
  };

  const handleRemoveItem = async (e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    if (
      !list ||
      !confirm(`Are you sure you want to remove this ${isAddressList ? 'address' : 'item'} from the list?`)
    ) {
      return;
    }
    await removeItem({
      variables: { input: { listId: list.id, value } },
    });
    refetch();
  };

  const handleExport = () => {
    if (!list) return;

    // Create CSV content
    const headers = isAddressList
      ? ['Address', 'Entity', 'Added']
      : ['Value', 'Added'];

    const rows = list.items.edges.map(({ node: item }) => {
      if (isAddressList) {
        return [
          item.value?.address || '',
          item.value?.entity?.name || '',
          new Date(item.createdAt).toLocaleDateString(),
        ];
      }
      return [
        item.value?.address || '',
        new Date(item.createdAt).toLocaleDateString(),
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${list.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Flex align="center" justify="center" py="9">
        <Spinner size="3" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex align="center" justify="center" py="9">
        <Text color="red">Error loading list: {error.message}</Text>
      </Flex>
    );
  }

  if (!list) {
    return (
      <Flex align="center" justify="center" direction="column" gap="4" py="9">
        <Text size="4" color="gray">
          List not found
        </Text>
        <Button variant="soft" onClick={() => navigate('/lists')}>
          <ArrowLeftIcon />
          Back to Lists
        </Button>
      </Flex>
    );
  }

  return (
    <DetailPageLayout
      backPath="/lists"
      backText="Back to Lists"
      icon={<ListBulletIcon width={24} height={24} />}
      iconBackgroundColor="var(--blue-3)"
      iconColor="var(--blue-11)"
      title={list.name}
      subtitle={
        <Flex direction="column" gap="2">
          {list.description && (
            <Text size="2" color="gray">
              {list.description}
            </Text>
          )}
          <Flex align="center" gap="4">
            <Flex align="center" gap="1">
              <PersonIcon width={14} height={14} />
              <Text size="2" weight="medium">
                {list.items.totalCount.toLocaleString()} {isAddressList ? 'addresses' : 'items'}
              </Text>
            </Flex>
            <Flex align="center" gap="1">
              <CalendarIcon width={14} height={14} />
              <Text size="2" color="gray">
                Created {new Date(list.createdAt).toLocaleDateString()}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      }
      badge={
        list.isSystem ? (
          <Tooltip content="System-managed list (read-only)">
            <Badge color="gray" variant="soft">
              <LockClosedIcon width={12} height={12} />
              Read-only
            </Badge>
          </Tooltip>
        ) : undefined
      }
      actions={
        <Flex gap="2">
          <Button variant="soft" onClick={handleExport}>
            <DownloadIcon />
            Export
          </Button>
          {!list.isSystem && (
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <PlusIcon />
              {isAddressList ? 'Add Address' : 'Add Item'}
            </Button>
          )}
        </Flex>
      }
    >

      {/* Items Table */}
      <Card size="2">
        {list.items.edges.length === 0 ? (
          <Flex align="center" justify="center" py="6">
            <Text color="gray">No {isAddressList ? 'addresses' : 'items'} in this list yet.</Text>
          </Flex>
        ) : (
          <Table.Root size="2">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>
                  <Flex
                    align="center"
                    gap="1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('address')}
                  >
                    {isAddressList ? 'Address' : 'Value'}
                    {sortColumn === 'address' && (
                      sortDirection === SortDirection.Asc ? <CaretUpIcon /> : <CaretDownIcon />
                    )}
                  </Flex>
                </Table.ColumnHeaderCell>
                {isAddressList && (
                  <Table.ColumnHeaderCell>
                    <Flex
                      align="center"
                      gap="1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('entity')}
                    >
                      Entity
                      {sortColumn === 'entity' && (
                        sortDirection === SortDirection.Asc ? <CaretUpIcon /> : <CaretDownIcon />
                      )}
                    </Flex>
                  </Table.ColumnHeaderCell>
                )}
                {isAddressList && (
                  <Table.ColumnHeaderCell>
                    24h Activity
                  </Table.ColumnHeaderCell>
                )}
                <Table.ColumnHeaderCell>
                  <Flex
                    align="center"
                    gap="1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('added')}
                  >
                    Added
                    {sortColumn === 'added' && (
                      sortDirection === SortDirection.Asc ? <CaretUpIcon /> : <CaretDownIcon />
                    )}
                  </Flex>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="60px" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {list.items.edges.map(({ node: item }) => {
                const addressString = item.value?.address || '';
                return (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      {isAddressList && item.value ? (
                        <AddressDisplay address={item.value} showEntityName={false} />
                      ) : (
                        <Text size="2" style={{ fontFamily: 'monospace' }}>
                          {addressString}
                        </Text>
                      )}
                    </Table.Cell>
                    {isAddressList && (
                      <Table.Cell>
                        {item.value?.entity ? (
                          <EntityDisplay
                            id={item.value.entity.id}
                            name={item.value.entity.name}
                            risks={item.value.entity.risks?.edges?.map(e => e.node) ?? []}
                          />
                        ) : (
                          <Text size="2" color="gray">
                            -
                          </Text>
                        )}
                      </Table.Cell>
                    )}
                    {isAddressList && (
                      <Table.Cell>
                        <Text size="2" color="gray">
                          -
                        </Text>
                      </Table.Cell>
                    )}
                    <Table.Cell>
                      <Text size="2" color="gray">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {!list.isSystem && (
                        <Flex gap="1" justify="end">
                          <Tooltip content="Remove from list">
                            <IconButton
                              size="1"
                              variant="ghost"
                              color="red"
                              onClick={(e) => handleRemoveItem(e, addressString)}
                            >
                              <TrashIcon width={14} height={14} />
                            </IconButton>
                          </Tooltip>
                        </Flex>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        )}
      </Card>

      {/* Add Item Dialog */}
      <Dialog.Root open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <Dialog.Portal>
          <Theme appearance={effectiveTheme} asChild>
            <div>
              <Dialog.Overlay
                style={{
                  backgroundColor: 'var(--black-a9)',
                  position: 'fixed',
                  inset: 0,
                  animation: 'overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
              <Dialog.Content
                style={{
                  backgroundColor: 'var(--color-background)',
                  borderRadius: '8px',
                  boxShadow:
                    'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '90vw',
                  maxWidth: '450px',
                  maxHeight: '85vh',
                  padding: '24px',
                }}
              >
                <Flex align="center" justify="between" mb="4">
                  <Dialog.Title asChild>
                    <Heading size="4">{isAddressList ? 'Add Address' : 'Add Item'}</Heading>
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <IconButton variant="ghost" color="gray" size="2">
                      <Cross2Icon width={18} height={18} />
                    </IconButton>
                  </Dialog.Close>
                </Flex>

                <form onSubmit={handleAddItem}>
                  <Flex direction="column" gap="4">
                    <Box>
                      <Text as="label" size="2" weight="medium" mb="1">
                        {isAddressList ? 'Ethereum Address' : 'Value'}
                      </Text>
                      <TextField.Root
                        placeholder={isAddressList ? '0x...' : 'Enter value'}
                        value={newItemValue}
                        onChange={(e) => setNewItemValue(e.target.value)}
                        style={{ fontFamily: 'monospace' }}
                      />
                      {isAddressList && (
                        <Text size="1" color="gray" mt="1">
                          Enter a valid Ethereum address (0x followed by 40 hex
                          characters)
                        </Text>
                      )}
                    </Box>

                    {addError && (
                      <Text color="red" size="2">
                        {addError}
                      </Text>
                    )}

                    <Flex gap="3" justify="end" mt="2">
                      <Dialog.Close asChild>
                        <Button type="button" variant="soft" color="gray">
                          Cancel
                        </Button>
                      </Dialog.Close>
                      <Button
                        type="submit"
                        disabled={addingItem || !newItemValue.trim()}
                      >
                        {addingItem ? 'Adding...' : isAddressList ? 'Add Address' : 'Add Item'}
                      </Button>
                    </Flex>
                  </Flex>
                </form>
              </Dialog.Content>
            </div>
          </Theme>
        </Dialog.Portal>
      </Dialog.Root>
    </DetailPageLayout>
  );
};

export default ListDetail;
